import { useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download } from "lucide-react";
import { downloadExport } from "./shared/downloadExport";
import { PaginationBar } from "@/components/shared/PaginationBar";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

function defaultFrom() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function DieselByEquipment({ site }) {
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    if (!site) return toast.error("Select a site first");
    setLoading(true);
    client
      .get("/diesel-register/by-equipment", { params: { site, date_from: dateFrom, date_to: dateTo, page: pageNum, page_size: 50 } })
      .then((res) => {
        setRows(res.data.items);
        setTotalPages(res.data.total_pages);
        setPage(res.data.page);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const exportXlsx = async () => {
    if (!site) return toast.error("Select a site first");
    try {
      await downloadExport("/diesel-register/export/by-equipment", { site, date_from: dateFrom, date_to: dateTo });
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="diesel-by-equipment">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="date" data-testid="diesel-by-equipment-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" data-testid="diesel-by-equipment-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" />
        <Button onClick={() => load(1)} disabled={loading || !site} data-testid="diesel-by-equipment-load" className="bg-amber-600 hover:bg-amber-700">
          {loading ? "Loading..." : "Load"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!site} data-testid="diesel-by-equipment-export">
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {rows && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="diesel-by-equipment-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Total Issued</TableHead>
                  <TableHead>Fills</TableHead>
                  <TableHead>Hours Run</TableHead>
                  <TableHead>Litres/Hour</TableHead>
                  <TableHead>Last Issued</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                      No issued entries in this range.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={`${r.equipment_type}-${r.equipment_ref}`} data-testid={`diesel-by-equipment-row-${r.equipment_ref}`}>
                      <TableCell className="text-sm font-medium">
                        {r.equipment_label} {r.is_hired && <Badge className="bg-sky-600 ml-1">Hired</Badge>}
                      </TableCell>
                      <TableCell className="font-mono">{fmt(r.total_issued)} L</TableCell>
                      <TableCell>{r.fills_count}</TableCell>
                      <TableCell className="font-mono">{r.hours_run != null ? fmt(r.hours_run) : "—"}</TableCell>
                      <TableCell className="font-mono">{r.litres_per_hour != null ? r.litres_per_hour : "—"}</TableCell>
                      <TableCell className="text-xs">{r.last_issued_date ? new Date(r.last_issued_date).toLocaleDateString("en-IN") : "—"}</TableCell>
                      <TableCell>{r.unreliable && <Badge className="bg-amber-500">Check readings</Badge>}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="diesel-by-equipment-pagination" />
        </>
      )}
    </div>
  );
}
